import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MessageSquare, Plus, Filter, Search, AlertTriangle,
  Clock, CheckCircle2, XCircle, User, Briefcase, ArrowRight
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import { toast } from 'sonner';
import { format, differenceInHours } from 'date-fns';

const TicketsManagementPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ticketsRes, statsRes] = await Promise.all([
        api.get('/tickets-v2'),
        api.get('/tickets-v2/stats')
      ]);
      setTickets(ticketsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      urgent: 'bg-red-600 text-white',
      high: 'bg-orange-500 text-white',
      normal: 'bg-blue-500 text-white',
      low: 'bg-gray-500 text-white'
    };
    const labels = {
      urgent: i18n.language === 'ar' ? 'عاجل' : 'Urgent',
      high: i18n.language === 'ar' ? 'عالي' : 'High',
      normal: i18n.language === 'ar' ? 'عادي' : 'Normal',
      low: i18n.language === 'ar' ? 'منخفض' : 'Low'
    };
    return <Badge className={colors[priority]}>{labels[priority]}</Badge>;
  };

  const getStatusBadge = (status) => {
    const colors = {
      new: 'bg-blue-500 text-white',
      in_progress: 'bg-yellow-500 text-white',
      replied: 'bg-green-500 text-white',
      closed: 'bg-gray-500 text-white',
      reopened: 'bg-purple-500 text-white'
    };
    const labels = {
      new: i18n.language === 'ar' ? 'جديدة' : 'New',
      in_progress: i18n.language === 'ar' ? 'قيد المعالجة' : 'In Progress',
      replied: i18n.language === 'ar' ? 'تم الرد' : 'Replied',
      closed: i18n.language === 'ar' ? 'مغلقة' : 'Closed',
      reopened: i18n.language === 'ar' ? 'معاد فتحها' : 'Reopened'
    };
    return <Badge className={colors[status]}>{labels[status]}</Badge>;
  };

  const getSLAIndicator = (ticket) => {
    if (!ticket.sla_due_time || ticket.status === 'closed') return null;
    
    const now = new Date();
    const dueTime = new Date(ticket.sla_due_time);
    const hoursRemaining = differenceInHours(dueTime, now);
    
    if (ticket.is_overdue) {
      return (
        <div className="flex items-center gap-1 text-red-600 text-sm font-semibold">
          <AlertTriangle className="w-4 h-4" />
          <span>{i18n.language === 'ar' ? 'متأخرة' : 'Overdue'}</span>
        </div>
      );
    } else if (hoursRemaining <= 2) {
      return (
        <div className="flex items-center gap-1 text-orange-600 text-sm font-semibold">
          <Clock className="w-4 h-4" />
          <span>{hoursRemaining}h</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1 text-gray-600 text-sm">
          <Clock className="w-4 h-4" />
          <span>{hoursRemaining}h</span>
        </div>
      );
    }
  };

  const filteredTickets = tickets.filter(t => {
    const matchesStatus = filterStatus === 'all' || t.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || t.priority === filterPriority;
    const matchesSearch = t.ticket_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         t.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesPriority && matchesSearch;
  });

  const statsCards = [
    {
      title: i18n.language === 'ar' ? 'إجمالي التذاكر' : 'Total Tickets',
      value: stats?.total || 0,
      icon: MessageSquare,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: i18n.language === 'ar' ? 'جديدة' : 'New',
      value: stats?.new || 0,
      icon: Plus,
      color: 'from-cyan-500 to-cyan-600'
    },
    {
      title: i18n.language === 'ar' ? 'قيد المعالجة' : 'In Progress',
      value: stats?.in_progress || 0,
      icon: Clock,
      color: 'from-yellow-500 to-yellow-600'
    },
    {
      title: i18n.language === 'ar' ? 'عاجلة' : 'Urgent',
      value: stats?.urgent || 0,
      icon: AlertTriangle,
      color: 'from-red-500 to-red-600'
    },
    {
      title: i18n.language === 'ar' ? 'متأخرة' : 'Overdue',
      value: stats?.overdue || 0,
      icon: XCircle,
      color: 'from-orange-500 to-orange-600'
    },
    {
      title: i18n.language === 'ar' ? 'مغلقة' : 'Closed',
      value: stats?.closed || 0,
      icon: CheckCircle2,
      color: 'from-green-500 to-green-600'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">
                {i18n.language === 'ar' ? 'إدارة التذاكر' : 'Tickets Management'}
              </h1>
              <p className="text-gray-600 text-lg">
                {i18n.language === 'ar' ? 'نظام التذاكر الشامل مع SLA وإدارة الأولويات' : 'Comprehensive ticketing system with SLA and priority management'}
              </p>
            </div>
            {user?.role === 'client' && (
              <Button onClick={() => navigate('/tickets/create')} className="btn-primary gap-2">
                <Plus className="w-5 h-5" />
                {i18n.language === 'ar' ? 'تذكرة جديدة' : 'New Ticket'}
              </Button>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {statsCards.map((stat, index) => (
              <Card key={index} className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all">
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5`} />
                <CardContent className="pt-6 relative">
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-xs text-gray-600 font-medium">{stat.title}</p>
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Filters */}
          <Card className="shadow-lg">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    placeholder={i18n.language === 'ar' ? 'بحث...' : 'Search...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{i18n.language === 'ar' ? 'جميع الحالات' : 'All Statuses'}</SelectItem>
                    <SelectItem value="new">{i18n.language === 'ar' ? 'جديدة' : 'New'}</SelectItem>
                    <SelectItem value="in_progress">{i18n.language === 'ar' ? 'قيد المعالجة' : 'In Progress'}</SelectItem>
                    <SelectItem value="replied">{i18n.language === 'ar' ? 'تم الرد' : 'Replied'}</SelectItem>
                    <SelectItem value="closed">{i18n.language === 'ar' ? 'مغلقة' : 'Closed'}</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{i18n.language === 'ar' ? 'جميع الأولويات' : 'All Priorities'}</SelectItem>
                    <SelectItem value="urgent">{i18n.language === 'ar' ? 'عاجل' : 'Urgent'}</SelectItem>
                    <SelectItem value="high">{i18n.language === 'ar' ? 'عالي' : 'High'}</SelectItem>
                    <SelectItem value="normal">{i18n.language === 'ar' ? 'عادي' : 'Normal'}</SelectItem>
                    <SelectItem value="low">{i18n.language === 'ar' ? 'منخفض' : 'Low'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Tickets List */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>{i18n.language === 'ar' ? 'قائمة التذاكر' : 'Tickets List'}</CardTitle>
            </CardHeader>
            <CardContent>
              {filteredTickets.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">{i18n.language === 'ar' ? 'لا توجد تذاكر' : 'No tickets found'}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredTickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      onClick={() => navigate(`/tickets/${ticket.id}`)}
                      className={`p-4 border rounded-xl hover:shadow-lg transition-all cursor-pointer ${
                        ticket.is_overdue ? 'border-l-4 border-l-red-500 bg-red-50/30' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h4 className="font-bold text-gray-900">{ticket.ticket_number}</h4>
                            {getStatusBadge(ticket.status)}
                            {getPriorityBadge(ticket.priority)}
                            {getSLAIndicator(ticket)}
                          </div>
                          <h5 className="text-base font-semibold text-gray-800 mb-2">{ticket.title}</h5>
                          <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                            <span className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {ticket.client_name}
                            </span>
                            {ticket.case_number && (
                              <span className="flex items-center gap-1">
                                <Briefcase className="w-4 h-4" />
                                {ticket.case_number}
                              </span>
                            )}
                            {ticket.assigned_to_name && (
                              <span className="flex items-center gap-1 text-blue-600">
                                <User className="w-4 h-4" />
                                {ticket.assigned_to_name}
                              </span>
                            )}
                            <span>{format(new Date(ticket.created_at), 'dd/MM/yyyy HH:mm')}</span>
                          </div>
                        </div>
                        <Button size="sm" variant="outline">
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TicketsManagementPage;